import { useEffect, useState } from 'react'
import type { EmailOtpType } from '@supabase/supabase-js'
import { useApp } from '../store/AppContext'
import { authLinkError, supabase } from '../lib/supabase'

/**
 * Where a password-reset or invite link lands.
 *
 * A working link signs the person in before this renders, which is why the
 * screen has to block everything else: a half-finished recovery would
 * otherwise drop them into the app still not knowing their password.
 *
 * A link that did NOT work lands here too, though, because the route is the
 * same. That case used to show the form anyway, and the person found out only
 * after choosing a password twice and being told "Auth session missing!" --
 * the library's words for a problem that happened before they typed anything.
 * So the session is checked first, and a spent link says so up front.
 */

/** What a failed link means, in words the person can act on. */
function explainLink(code: string | null, description: string | null): string {
  if (code === 'otp_expired' || code === 'access_denied') {
    return 'That link has already been used, or it has expired. Each one works once.'
  }
  if (description) return description
  return 'That link did not sign you in. It may have already been used, or expired.'
}
export function SetPasswordScreen() {
  const { signOut } = useApp()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [link, setLink] = useState<'checking' | 'ok' | 'dead'>('checking')

  useEffect(() => {
    let cancelled = false

    void (async () => {
      /*
       * Two shapes of link arrive here, and the difference is the whole
       * reason this screen kept reporting spent links.
       *
       * The old one is Supabase's ConfirmationURL: a GET to /auth/v1/verify
       * that redeems the token AS IT IS FETCHED. Anything that merely opens
       * the URL spends it -- and corporate mail systems open every link in
       * every message to scan it, so the token was routinely gone before the
       * person clicked. Nothing on this end could tell that apart from a
       * genuine second visit.
       *
       * The new one carries token_hash and is redeemed HERE, by a POST this
       * page makes. A scanner fetching the page runs no JavaScript and sends
       * no POST, so the token survives for the person it was sent to.
       *
       * Both are handled, so links already sitting in inboxes keep working.
       */
      const params = new URLSearchParams(window.location.search)
      const tokenHash = params.get('token_hash')
      const type = params.get('type')

      const KINDS: EmailOtpType[] = ['recovery', 'invite', 'magiclink', 'signup', 'email']

      if (tokenHash && type && (KINDS as string[]).includes(type)) {
        const { error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: type as EmailOtpType,
        })

        // Taken out of the address bar either way: by now it really has been
        // spent, and a refresh should not look like a second attempt.
        window.history.replaceState({}, '', window.location.pathname)

        if (!cancelled) setLink(error ? 'dead' : 'ok')
        return
      }

      // getSession waits for the URL to have been processed, so by the time
      // it answers, an old-style link has already become a session.
      const { data } = await supabase.auth.getSession()
      if (!cancelled) setLink(data.session ? 'ok' : 'dead')
    })()

    return () => {
      cancelled = true
    }
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return

    if (password.length < 8) {
      setErr('Use at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setErr('The two passwords do not match.')
      return
    }

    setBusy(true)
    const { error } = await supabase.auth.updateUser({ password })
    setBusy(false)

    if (error) {
      setErr(error.message)
      return
    }
    setErr('')
    setDone(true)
  }

  return (
    <div className="auth">
      <div className="auth-brandside">
        <div className="row" style={{ gap: 10 }}>
          <div className="brandmark brandmark--lg">C</div>
          <div style={{ fontWeight: 600, letterSpacing: '-.01em', fontSize: 16 }}>Cadence</div>
        </div>
        <div style={{ maxWidth: 420 }}>
          <div className="auth-eyebrow">Almost there</div>
          <h1>Choose a password.</h1>
          <p>
            Pick something you don’t use anywhere else. You’ll use it with your email address to
            sign in from now on.
          </p>
        </div>
        <div />
      </div>

      <div className="auth-formside">
        <div className="auth-form">
          {link === 'checking' ? (
            <p style={{ color: 'var(--mut)' }}>Checking your link…</p>
          ) : link === 'dead' ? (
            <>
              <h2 style={{ fontSize: 20 }}>This link no longer works</h2>
              <p style={{ marginBottom: 12 }}>
                {explainLink(authLinkError?.code ?? null, authLinkError?.description ?? null)}
              </p>
              <p style={{ marginBottom: 16, fontSize: 13, color: 'var(--mut)' }}>
                If you never opened it, a security scanner on your mail system may have opened it
                for you — some check links before you see them, which uses the one visit up.
              </p>
              <button
                type="button"
                className="btn btn--primary"
                style={{ width: '100%' }}
                onClick={() => window.location.replace('/')}
              >
                Go to sign in
              </button>
              <p style={{ marginTop: 12, fontSize: 13, color: 'var(--mut)' }}>
                Use “Forgot your password?” there to send yourself a fresh link, or ask whoever
                invited you for a new one.
              </p>
            </>
          ) : done ? (
            <>
              <h2 style={{ fontSize: 20 }}>Password set</h2>
              <p style={{ marginBottom: 16 }}>
                You’re signed in. If an administrator has not approved your access yet, you’ll see
                that on the next screen.
              </p>
              <button
                type="button"
                className="btn btn--primary"
                style={{ width: '100%' }}
                onClick={() => window.location.replace('/')}
              >
                Continue
              </button>
            </>
          ) : (
            <form onSubmit={(e) => void submit(e)}>
              <h2 style={{ fontSize: 20 }}>Set your password</h2>
              <p style={{ marginBottom: 16 }}>At least 8 characters.</p>

              <label className="field">
                <span>New password</span>
                <input
                  autoFocus
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                />
              </label>

              <label className="field">
                <span>Confirm password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Type it again"
                />
              </label>

              {err && (
                <p style={{ color: 'var(--dg)', fontSize: 13, margin: '0 0 12px' }} role="alert">
                  {err}
                </p>
              )}

              <button type="submit" className="btn btn--primary" style={{ width: '100%' }} disabled={busy}>
                {busy ? 'Saving…' : 'Set password'}
              </button>

              <div className="auth-swap">
                <button type="button" onClick={() => void signOut()}>
                  Cancel and sign out
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
